import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { HyperLink } from '@src/types/contentful';
import { ButtonIcon } from '@src/components/Button';
import MobileMenuAccordion from './MobileMenuAccordion';
import styles from './MobileMenu.module.scss';
import { COLOR_WHITE } from '@src/types/colors';
import { Maybe } from '@src/graphql/gql-types';
import EnquireButton from '@src/components/Header/EnquireButton';
import { useWindow } from '@src/hooks/useWindow';
import { ButtonLinkStandard } from '@src/components/ButtonLink';
import BoxIcon from '@src/elements/BoxIcon';

export interface MobileMenu {
  menuLinks: HyperLink[];
  mobileMenuState: {
    open: boolean;
    focusSearch: boolean;
  };
  enquireButtonScrollTo: string;
  setMobileMenuState: Dispatch<
    SetStateAction<{ open: boolean; focusSearch: boolean }>
  >;
  headOfficePhoneNumber?: Maybe<string>;
}

const MobileMenu: React.FC<MobileMenu> = ({
  menuLinks,
  mobileMenuState,
  setMobileMenuState,
  headOfficePhoneNumber,
  enquireButtonScrollTo
}) => {
  const { document } = useWindow();

  const handleCloseMobileMenu = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      // prevent accordion expanding/collapsing when clicking the AccordionItemButton link
      event.stopPropagation();
      setMobileMenuState(() => ({
        open: false,
        focusSearch: false
      }));
    },
    [setMobileMenuState]
  );

  useEffect(() => {
    if (document) {
      if (mobileMenuState.open) {
        (
          document.getElementsByTagName('body')[0] as HTMLBodyElement
        ).style.overflow = 'hidden';
      } else {
        (
          document.getElementsByTagName('body')[0] as HTMLBodyElement
        ).style.overflow = 'auto';
      }
    }
  }, [document, mobileMenuState]);

  return (
    <div
      className={classNames(styles.mobileMenu, {
        [styles.mobileMenuOpen]: mobileMenuState.open
      })}
    >
      <div className={styles.mobileMenuInner}>
        <div className={styles.mobileMenuTopBar}>
          <ButtonIcon
            data-testid="mobileMenuCloseButton"
            size="lg"
            isBorderless
            fillVariant="outline"
            color={COLOR_WHITE}
            boxIcon="x"
            onClick={handleCloseMobileMenu}
            aria-label="Close the menu"
          />
        </div>

        <div className={styles.mobileMenuControls}>
          <EnquireButton
            testid="mobileMenuEnquireButton"
            scrollToId={enquireButtonScrollTo}
            handleCloseMobileMenu={handleCloseMobileMenu}
          />
          {headOfficePhoneNumber && (
            <ButtonLinkStandard
              data-testid="headerPhoneNumberButton"
              color={COLOR_WHITE}
              aria-label="Phone"
              size="md"
              href={`tel:${headOfficePhoneNumber}`}
              className={styles.phoneButton}
            >
              {/* TODO: move to ButtonLinkStandard "icon" prop once that's switched to BoxIcon */}
              <BoxIcon name="phone" size="sm" />
              {headOfficePhoneNumber}
            </ButtonLinkStandard>
          )}
        </div>
        <MobileMenuAccordion
          menuLinks={menuLinks}
          handleCloseMobileMenu={handleCloseMobileMenu}
        />
      </div>
    </div>
  );
};

export default MobileMenu;
